import {ClientGate} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import {BnplPaymentSorting} from '../components/PaymentSlots/BnplPaymentSorting';
import {BnplSlots, BnplSlotsProps} from '../components/PaymentSlots/BnplSlots';
import {
  ProductDetailsSlots,
  ProductDetailsSlotsProps,
} from '../components/ProductDetailsSlots/ProductDetailsSlots';
import {HomeDeliveryFeePriceRow} from '../components/SustainabilityBadge/HomeDeliveryFeePriceRow';
import {
  SustainabilitySlots,
  SustainabilitySlotsProps,
} from '../components/SustainabilityBadge/SustainabilitySlots';
import {TotalPriceWithSecondaryPriceLabelSlot} from '../components/TotalPriceWithSecondaryPriceLabelSlot/TotalPriceWithSecondaryPriceLabelSlot';
import type {CheckoutExperiment} from '../hooks/useCheckoutEntryDetails';
import {ScayleStateListener} from '../scayle-state/ScayleStateListener';
import {CheckoutTrackingListener} from '../tracking/CheckoutTrackingListener';
import {ReportExpressCheckoutSwitch} from './ReportExpressCheckoutSwitch';
import {ReportPaymentLoadTiming} from './ReportPaymentLoadTiming';
import {CheckoutElement} from './types';

type SharedProps = {
  origin: string;
  showSecondaryPriceLabel: boolean;
  hasDatadogConsent: boolean;
};

type RawWebComponentProps = SharedProps & {
  jwt?: string;
  appId?: number;
  correlationId?: string;
  accessToken?: string;
  authToken: string;
  transactionId?: null;
  showSecondaryPriceLabel: boolean;
  SustainabilityInfoLayoutComponent: SustainabilitySlotsProps['SustainabilityInfoLayoutComponent'];
  BnplInfoLayoutComponent: BnplSlotsProps['BnplInfoLayoutComponent'];
  ProductDetailsLayoutComponent: ProductDetailsSlotsProps['ProductDetailsLayoutComponent'];
  UnderPaymentPrivacyNotificationComponent: FC;
  hasDatadogConsent: boolean;
  experiments: CheckoutExperiment[];
  appClientVersion?: string;
};

type RawWebComponentWithTransactionIdProps = SharedProps & {
  transactionId: string;
};

const animationDurationMs = 800;
export const WebComponentStyles: {
  ANIMATION_DURATION_MS: number;
  initial: React.CSSProperties;
  loaded: React.CSSProperties;
  afterLoaded: React.CSSProperties;
} = {
  ANIMATION_DURATION_MS: animationDurationMs,
  initial: {
    opacity: 0,
    position: 'absolute',
    left: 0,
    top: 0,
    display: 'block',
    transition: `transform ${animationDurationMs}ms ease-in-out, opacity ${animationDurationMs}ms ease-in-out`,
    transformOrigin: 'center 15%',
    transform: 'scale(0.96)',
  },
  loaded: {
    opacity: 1,
    position: 'relative',
    height: 'unset',
    transform: 'scale(1)',
  },
  afterLoaded: {
    transform: 'unset',
  },
};

export const RawWebComponent = (
  props: (RawWebComponentProps | RawWebComponentWithTransactionIdProps) & {
    ref?: React.Ref<CheckoutElement>;
  },
) => {
  const {ref} = props;
  if (props.transactionId) {
    const {transactionId, origin, showSecondaryPriceLabel, hasDatadogConsent} =
      props;
    return (
      <>
        <scayle-checkout
          key={transactionId ?? undefined}
          style={WebComponentStyles.initial}
          data-testid="scayle-checkout"
          origin={origin}
          headerElement="#header"
          data-dd-privacy="mask"
          transaction-id={transactionId}
          has-datadog-consent={hasDatadogConsent.toString()}
          ref={ref as any}
        >
          {showSecondaryPriceLabel && (
            <TotalPriceWithSecondaryPriceLabelSlot webComponentRef={ref} />
          )}
        </scayle-checkout>
        <CheckoutTrackingListener isServerRendered={false} />
        <ScayleStateListener isServerRendered={false} />
      </>
    );
  }

  const {
    accessToken,
    jwt,
    appId,
    origin,
    correlationId,
    SustainabilityInfoLayoutComponent,
    BnplInfoLayoutComponent,
    ProductDetailsLayoutComponent,
    UnderPaymentPrivacyNotificationComponent,
    showSecondaryPriceLabel,
    experiments,
    authToken,
    appClientVersion,
  } = props as RawWebComponentProps;

  if (!accessToken) {
    throw new Error('AccessToken must be provided');
  }

  const isApp = origin === 'app';

  return (
    <>
      <scayle-checkout
        key={jwt ?? undefined}
        style={WebComponentStyles.initial}
        access-token={accessToken}
        appId={appId}
        data-testid="scayle-checkout"
        origin={origin}
        correlationId={correlationId}
        headerElement="#header"
        jwt={jwt}
        data-dd-privacy="mask"
        has-datadog-consent={props.hasDatadogConsent.toString()}
        ref={ref as any}
      >
        <ClientGate>
          <SustainabilitySlots
            SustainabilityInfoLayoutComponent={
              SustainabilityInfoLayoutComponent
            }
          />

          <HomeDeliveryFeePriceRow />

          {showSecondaryPriceLabel && (
            <TotalPriceWithSecondaryPriceLabelSlot webComponentRef={ref} />
          )}

          <UnderPaymentPrivacyNotificationComponent />
          <BnplSlots
            BnplInfoLayoutComponent={BnplInfoLayoutComponent}
            experiments={experiments}
            authToken={authToken}
            appClientVersion={appClientVersion}
          />
          <ProductDetailsSlots
            ProductDetailsLayoutComponent={ProductDetailsLayoutComponent}
          />
        </ClientGate>
      </scayle-checkout>

      <CheckoutTrackingListener isServerRendered={isApp} />
      <ScayleStateListener isServerRendered={isApp} />
      <BnplPaymentSorting experiments={experiments} />
      <ReportPaymentLoadTiming experiments={experiments} />
      <ReportExpressCheckoutSwitch />
    </>
  );
};

/**
 * Type of the JSX element representing the web components.
 */
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      ['scayle-checkout']: {
        key?: string;
        'access-token'?: string;
        appId?: number;
        origin: string;
        style?: object;
        correlationId?: string;
        ref?: React.RefObject<HTMLDivElement | null>;
        headerElement?: string; // query selector
        'data-testIi'?: string;
        'transaction-id'?: string;
        jwt?: string;
        children?: React.ReactNode;
        'has-datadog-consent': string;
      };
    }
  }
}
